import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "modal__close-wrap" }
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["id"]
const _hoisted_6 = ["aria-label"]

import { ref, onUpdated, onMounted, watch, nextTick } from 'vue';
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useFocusTrap } from "@features/Pages/TimelineMapPage/_js/app/composables/useFocusTrap";


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
  // If we don't want a link to trigger the showing of the modal
  show: Boolean,
  id: String,
},
  emits: ["closeModal", "showModal"],
  setup(__props, { emit: __emit }) {

const store = useTimelineMapStore();

const props = __props;

const emit = __emit

const dialog = ref<HTMLDialogElement>();
const mmDialog = ref<HTMLElement | null>(null);
const { enable: trapOn, disable: trapOff } = useFocusTrap(mmDialog, {
  includeRoot: true,
  initialFocus: () => mmDialog.value,
  onEscape: () => onCloseModal(),
  returnFocus: true,
});

const showModal = ref(false);

watch(() => store.showModal, () => {
  if (store.showModal) {
    onShowModal();
  } else {
    onCloseModal();
  }
});

const onShowModal = async () => { 
  showModal.value = true;

  if (store.isVRAK) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--vrak-scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('disable-scroll-vrak');
  } else {
    document.body.classList.add('disable-scroll');
  }
  
  emit('showModal'); // In case someone wants to listen ¯\_(ツ)_/¯
  
  if (store.isMM || store.isVRAK) {
    await nextTick();
    trapOn();
  }
};

const onCloseModal = () => {
  showModal.value = false;

  if (store.isVRAK) {
    document.body.classList.remove('disable-scroll-vrak');
    document.body.style.removeProperty('--vrak-scrollbar-width');
  } else {
    document.body.classList.remove('disable-scroll');
  }
  window.removeEventListener('resize', onResize);

  emit('closeModal'); // In case someone wants to listen ¯\_(ツ)_/¯
  if (store.isMM || store.isVRAK) { trapOff(); }
}

onMounted(() => {
  if (props.show) {
    onShowModal();
  }
  if (store.isJVM) {
    closeModalOnBackdropClickListener();
  }
});

onUpdated(() => {
  // Delay so modal is completely rendered and we'll get an accurate height
  setTimeout(() => {
    setScrollStyle();
  }, 300);
});

// Listens to clicks outside the modal, and closes it
const closeModalOnBackdropClickListener = () => {
  dialog.value?.addEventListener('click', ((event) => {
    let rect = (event?.target as HTMLElement)?.getBoundingClientRect();

    if (rect.left > event.clientX ||
      rect.right < event.clientX ||
      rect.top > event.clientY ||
      rect.bottom < event.clientY) {
      onCloseModal();
    }
  }));
}

function onResize() {
  setTimeout(() => {
    setScrollStyle();
  }, 200);
}

// Checks if dialog is taller than the screen, and if so, adds a class to it that enables vertical scroll
function setScrollStyle() {
  if (!dialog.value) {
    return;
  }

  const children = dialog.value.children;
  let modalHeight = 0;

  if (children instanceof HTMLCollection) {
    modalHeight = Array.from(children).reduce((sum, child) => {
      return sum + (child as HTMLLIElement).offsetHeight
    }, 0);
  } else {
    modalHeight = dialog.value.offsetHeight;
  }

  if (modalHeight > window.innerHeight) {
    // Modal is taller than window, add scroll
    dialog.value.classList.add('modal--scroll');
  } else {
    dialog.value.classList.remove('modal--scroll');
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "link", { clicked: onShowModal }),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createCommentVNode("Sticky close button"),
              (_unref(store).isMM || _unref(store).isVRAK)
                ? (_openBlock(), _createBlock(_Transition, {
                    key: 0,
                    name: "pop"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("section", {
                        ref_key: "mmDialog",
                        ref: mmDialog,
                        id: __props.id,
                        class: "modal",
                        "aria-modal": "true",
                        tabindex: "0"
                      }, [
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("button", {
                            class: "modal__close",
                            onClick: _withModifiers(onCloseModal, ["prevent"]),
                            onKeydown: [
                              _withKeys(onCloseModal, ["enter"]),
                              _withKeys(onCloseModal, ["space"])
                            ],
                            tabindex: "0",
                            "aria-label": _unref(store).translations.close
                          }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_4)
                        ]),
                        _renderSlot(_ctx.$slots, "content", { close: onCloseModal })
                      ], 8 /* PROPS */, _hoisted_2)
                    ]),
                    _: 3 /* FORWARDED */
                  }))
                : _createCommentVNode("v-if", true),
              _createCommentVNode("Not sticky close button"),
              (_unref(store).isJVM)
                ? (_openBlock(), _createElementBlock("section", {
                    key: 1,
                    ref_key: "dialog",
                    ref: dialog,
                    id: __props.id,
                    class: "modal",
                    "aria-modal": "true"
                  }, [
                    _createElementVNode("button", {
                      class: "modal__close icon icon--close",
                      onClick: _withModifiers(onCloseModal, ["prevent"]),
                      "aria-label": _unref(store).translations.close
                    }, _toDisplayString(_unref(store).translations.close), 9 /* TEXT, PROPS */, _hoisted_6),
                    _renderSlot(_ctx.$slots, "content", { close: onCloseModal })
                  ], 8 /* PROPS */, _hoisted_5))
                : _createCommentVNode("v-if", true),
              _createElementVNode("div", {
                class: "modal-backdrop",
                onClick: onCloseModal
              })
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 3 /* FORWARDED */
    })
  ], 64 /* STABLE_FRAGMENT */))
}
}

})